import {ChangeDetectionStrategy, Component, inject, input, OnInit} from '@angular/core';
import {KeyboardListenerDirective} from '@core/directives/keyboard-listener.directive';
import {IContentBuilderFieldColor} from '@core/models/content-builder-fields.model';
import {ModalsService} from '@core/services/modals/modals.service';
import {TranslateService} from '@ngx-translate/core';
import {
  ComoRegistrationModalComponent
} from '@shared/modals/como/como-registration-modal/como-registration-modal.component';
import {RegistrationStep} from '@shared/modals/como/como-registration-modal/registration-step.enum';
import {KioskAlertModalComponent} from '@shared/modals/kiosk-alert-modal/kiosk-alert-modal.component';
import {filter, switchMap, tap} from 'rxjs/operators';
import {MarketplaceStoreSelector} from "@common/selectors/marketplace.selector";
import {ComoSignOn} from '@core/models/como.model';
import {Observable, of} from "rxjs";
import {
  ComoSsoLoadingModalComponent
} from "@shared/modals/como/como-sso-loading-modal/como-sso-loading-modal.component";
import {AuthFirebaseService} from "@core/services/api/auth-firebase/auth-firebase.service";
import {ComoApiService} from "@core/services/api/como/como-api.service";
import {MarketplaceKeys} from "@config/keys/shop.keys";

@Component({
  selector: 'app-kiosk-code-scanner-block',
  imports: [KeyboardListenerDirective],
  template: `
    <input
      class="invisible"
      appKeyboardListener
      (textBuffered)="onCodeBuffered($event)"
      id="keyboardInput"
      type="text"
      [value]="typedText"
      readonly
    />
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KioskCodeScannerBlockComponent implements OnInit {
  private readonly comoApiService = inject(ComoApiService);
  private readonly modalsService = inject(ModalsService);
  private readonly translateService = inject(TranslateService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly authFirebaseService = inject(AuthFirebaseService);

  public accentColor = input<IContentBuilderFieldColor>();

  public redirectUrl = input<string>();

  private isValidating: boolean = false;
  private isErrorModalOpen: boolean = false;

  private registrationStep = RegistrationStep;
  public typedText = '';
  public errorMessage = '';

  ngOnInit() {
    this.modalsService
      .getData(KioskAlertModalComponent.handle)
      .pipe(filter(modal => !modal))
      .subscribe(() => {
        this.isErrorModalOpen = false;
      });
  }

  public onCodeBuffered(code: string) {
    if (this.isValidating || this.isErrorModalOpen) return;

    this.typedText = code;
    this.onSubmit(code);
  }

  private onSubmit(code: string): void {
    if (this.isValidating || this.isErrorModalOpen) return;

    this.isValidating = true;

    this.comoApiService.getComoUserFromTemporaryToken$(
      code,
      this.marketplaceSelector.marketplace[MarketplaceKeys.Id],
      true,
      '',
    ).pipe(
      switchMap(comoSignOn => this.handleSingleSignOnResponse(comoSignOn)),
    ).subscribe({
      error: () => this.handleErrorResponse(),
      next: () => this.redirectToUrl(),
    });

    // this.comoService.linkComoUser$({ phoneOrEmail: undefined, code }).subscribe({
    //   error: () => this.handleErrorResponse(),
    //   next: () => this.redirectToUrl(),
    // });
  }


  private handleSingleSignOnResponse(comoSignOn: ComoSignOn): Observable<any> {
    if (comoSignOn.firebase_sign_in_token) {
      return this.authFirebaseService
        .signInWithCustomToken(comoSignOn.firebase_sign_in_token)
        .pipe(tap(() => this.modalsService.close(ComoSsoLoadingModalComponent.handle)));
    }
    return of(null);
  }

  private redirectToUrl(): void {
    this.modalsService.open(ComoRegistrationModalComponent.handle, undefined, {
      goToURLOnClose: this.redirectUrl,
      activeStep: this.registrationStep.STEP_KIOSK_CONFIRMATION,
      accentColor: this.accentColor,
    });
  }

  private handleErrorResponse(): void {
    this.isValidating = false;
    this.isErrorModalOpen = true;

    this.showErrorDialog();
  }

  private showErrorDialog(): void {
    this.modalsService.open(KioskAlertModalComponent.handle, undefined, {
      icon: 'icon-warning-sign',
      message: this.translateService.instant('authentication.loyalty.unable-to-authenticate'),
      accentColor: this.accentColor,
    });
  }
}
